import { Listbox as HeadlessListbox } from '@headlessui/react';
import * as Popper from '@radix-ui/react-popper';
import classNames from 'classnames';
import { isEmpty, isFunction, isUndefined } from 'lodash-es';
import { CaretDown, CaretUp, Check } from 'phosphor-react';
import { useCallback, useMemo, type JSX } from 'react';
import type { FormFieldVariant } from '../form-field-variant';
import type { Option } from '../option';
import { toOption } from '../option';
import { useOptionContainerViewportAwareResize } from '../option-container-viewport-aware-resize';

type ListboxProps<Value> = {
  options: (Option<Value> | Value)[];
  toDisplayValue: (value: Value) => string;
  toKey: (value: Value) => string;
  variant?: FormFieldVariant;
  className?: string;
  disabled?: boolean;
  placeholder?: string | (() => JSX.Element);
  ariaLabel?: string; // required as wrapping a multi-value listbox in a label causes the dropdown to close on select

} & ({
  // single value with empty option
  addEmptyOption: true;
  multiple?: false;
  value: Value | undefined;
  onValueChange: (newValue: Value | undefined) => void;
} | {
  // single value without empty option
  addEmptyOption?: false;
  multiple?: false;
  value: Value | undefined;
  onValueChange: (newValue: Value) => void;
} | {
  // multi value
  addEmptyOption?: never;
  multiple: true;
  value: Value[];
  onValueChange: (newValues: Value[]) => void;
});

export function Listbox<Value>({
  options: availableOptions,
  toDisplayValue,
  toKey,
  variant = 'default',
  className,
  disabled = false,
  placeholder: placeholderFn = 'Bitte wählen',
  ariaLabel,
  value,
  onValueChange,
  addEmptyOption = true,
  multiple,
}: ListboxProps<Value>) {
  const { triggerRef, containerRef } = useOptionContainerViewportAwareResize<HTMLDivElement, HTMLDivElement>();

  const options = useMemo(() => availableOptions.map(toOption), [availableOptions]);

  const placeholder = useMemo(() => (
    <span className="tds-input-value-placeholder">{isFunction(placeholderFn) ? placeholderFn() : placeholderFn}</span>
  ), [placeholderFn]);

  const buttonText = useMemo(() => {
    if (isUndefined(value) || (multiple && isEmpty(value))) {
      return placeholder;
    }

    if (!multiple) {
      return toDisplayValue(value);
    }

    return value.map(toDisplayValue).join(', ');
  }, [multiple, placeholder, toDisplayValue, value]);

  const handleChange = useCallback(
    (value: Value[] | NonNullable<Value> | null) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onValueChange(value ?? undefined as any);
    }
    , [onValueChange],
  );

  return (
    <Popper.Root>
      <HeadlessListbox value={value ?? null} onChange={handleChange} disabled={disabled} multiple={multiple}>
        <Popper.Anchor className="inline-block" ref={triggerRef}>
          <HeadlessListbox.Button
            aria-label={ariaLabel}
            className={classNames('flex justify-between items-center tds-input-field', className, {
              'tds-input-field-default': variant === 'default',
              'tds-input-field-small': variant === 'small',
              'bg-contrast text-dark': !disabled,
              'cursor-default': disabled,
            })}
          >
            {({ open }: {open: boolean}) => (<>
              <div className={classNames('flex-1 text-left tds-input-value whitespace-nowrap overflow-hidden', {
                'tds-input-value-default': variant === 'default',
                'tds-input-value-small': variant === 'small',
              })}>
                {buttonText}
              </div>
              {open ? (
                <CaretUp size={16} className={classNames('tds-input-icon', { disabled })} />
              ) : (
                <CaretDown size={16} className={classNames('tds-input-icon', { disabled })} />
              )}
            </>)}
          </HeadlessListbox.Button>
        </Popper.Anchor>

        <Popper.Content ref={containerRef}>
          <HeadlessListbox.Options id="test-id" as="ul" className={classNames('mt-2 select-none tds-option-container tds-input-value', {
            'tds-input-value-default': variant === 'default',
            'tds-input-value-small': variant === 'small',
          })}>
            {addEmptyOption && !multiple ? (
              <HeadlessListbox.Option
                as="li"
                className="flex items-center justify-between tds-option-container-item aria-checked:pr-3"
                value={null}
              >
                {({ selected }: {selected: boolean}) => (<>
                    Keine Auswahl
                  {selected ? <Check size={14} className="tds-input-icon"/> : null}
                </>)}
              </HeadlessListbox.Option>
            ) : null}

            {options.map(({ value, disabled }) => (
              <HeadlessListbox.Option
                as="li"
                className="flex items-center justify-between tds-option-container-item aria-checked:pr-3"
                key={toKey(value)}
                value={value}
                disabled={disabled}
              >
                {({ selected }: {selected: boolean}) => (<>
                  {toDisplayValue(value)}
                  {selected ? <Check size={14} className="tds-input-icon"/> : null}
                </>)}
              </HeadlessListbox.Option>
            ))}
          </HeadlessListbox.Options>
        </Popper.Content>
      </HeadlessListbox>
    </Popper.Root>
  );
}
